import { client as braintreeClient, threeDSecure } from 'braintree-web';

import logger from '@eventbrite/client-logger';
import { CustomSubmissionError } from '@eventbrite/redux-form-tools';

import {
    getBraintreeClientToken as getBraintreeClientTokenApi,
    getBraintreeNonce,
} from '../api/braintreeClient';
import {
    BRAINTREE_STATUS_LOOKUP_ENROLLED,
    BRAINTREE_TRANSSTATUS_AUTHENTICATION_NOT_PERFORMED,
    MAX_AMOUNT_EXEMPTION_REQUEST,
    PAYMENT_METHODS,
} from '../constants';
import { isPaidOrder as isOrderToBePaid } from '../selectors/order';
import { gettext } from '@eventbrite/i18n';
import { jsonStringifyWithoutCircularDependencies } from '../utils/json';

const is3DSecureRequiredEvent = (currency) => ['EUR', 'GBP'].includes(currency);

const isCreditCardOrVaultPaymentMethod = (paymentData) =>
    [PAYMENT_METHODS.CREDIT, PAYMENT_METHODS.USER_INSTRUMENT].includes(
        paymentData.paymentMethod,
    );

const is3DSecure2Error = (status, transStatus) =>
    status === BRAINTREE_STATUS_LOOKUP_ENROLLED &&
    transStatus !== BRAINTREE_TRANSSTATUS_AUTHENTICATION_NOT_PERFORMED;

const getBraintreeNoncePromise = ({
    paymentData,
    currency,
    countryCode,
    amount,
    orderId,
    eventId,
    userCountry,
}) =>
    getBraintreeNonce({
        paymentData,
        currency,
        countryCode,
        amount,
        orderId,
        eventId,
        userCountry,
    })
        .then(({ response: braintreeNonceResponse }) => braintreeNonceResponse)
        .catch(() => ({ needs_three_domain_secure: false }));

export const shouldPerform3DS2Validation = ({ state, paymentData }) => {
    const {
        app: {
            featureFlags: { enable3DSecure2: enable3DSecure2FeatureFlag },
        },
        event: { currency },
    } = state;

    const isPaidOrder = isOrderToBePaid(state);
    const isCreditCardOrVault = isCreditCardOrVaultPaymentMethod(paymentData);

    return (
        enable3DSecure2FeatureFlag &&
        isPaidOrder &&
        isCreditCardOrVault &&
        is3DSecureRequiredEvent(currency)
    );
};

export const threeDomainSecurePromise = async ({
    dispatch,
    savePaymentMethods,
    handleOrderError,
    paymentData,
    currency,
    countryCode,
    amount,
    orderId,
    eventId,
    userCountry,
}) => {
    try {
        const braintreeNonceResponse = await getBraintreeNoncePromise({
            countryCode,
            paymentData,
            currency,
            amount,
            orderId,
            eventId,
            userCountry,
        });

        if (!braintreeNonceResponse.needs_three_domain_secure) {
            return;
        }

        const {
            response: { client_token: braintreeClientToken },
        } = await getBraintreeClientTokenApi(currency);

        const clientInstance = await braintreeClient.create({
            authorization: braintreeClientToken,
        });

        const threeDSecureInstance = await threeDSecure.create({
            client: clientInstance,
            version: 2,
        });

        const threeDSecureVerifyPayload = await threeDSecureInstance
            .verifyCard({
                amount,
                nonce: braintreeNonceResponse.nonce,
                exemptionRequested: amount < MAX_AMOUNT_EXEMPTION_REQUEST,
                onLookupComplete: (data, next) => {
                    const reactRoot =
                        document.querySelectorAll('.eds-modal')[0];

                    reactRoot.style.zIndex = -1;
                    reactRoot.children[0].style.zIndex = -1;
                    next();
                },
            })
            .finally(() => {
                // Restore original z-index values
                const reactRoot = document.querySelectorAll('.eds-modal')[0];

                if (reactRoot) {
                    reactRoot.style.zIndex = '';
                    reactRoot.children[0].style.zIndex = '';
                }
            })
            .catch((err) => {
                logger.error(
                    `Braintree 3DSecure2 error: ${err}. Full error: ${jsonStringifyWithoutCircularDependencies(
                        err,
                    )}`,
                );

                const genericErrorMessage = gettext(
                    'Validation failed. Try again or use another payment method',
                );

                throw genericErrorMessage;
            });

        const {
            threeDSecureInfo: {
                status,
                lookup: { transStatus },
            },
            nonce: verifiedNonce,
        } = threeDSecureVerifyPayload;

        // We use the status of the 3DSecure2 authentication to go through with the payment
        // or throw an error.
        if (is3DSecure2Error(status, transStatus)) {
            const genericErrorMessage = gettext(
                'Validation failed. Try again or use another payment method',
            );

            throw genericErrorMessage;
        }

        // EB-119006: See if we can remove mentions of paypal
        const paymentDataBraintree = {
            paymentMethod: PAYMENT_METHODS.NONCE_BRAINTREE,
            payment: { paypalBraintreeNonce: verifiedNonce },
            totalAmountInCents: paymentData.totalAmountInCents,
        };

        return savePaymentMethods({
            orderId,
            countryCode,
            paymentData: paymentDataBraintree,
            currency,
        });
    } catch (errorMessage) {
        const submissionError = new CustomSubmissionError(
            {
                _error: errorMessage,
            },
            {},
        );

        handleOrderError(dispatch, submissionError);
    }
};
